import { useId, useLayoutEffect, useRef, useState } from 'react';

type Side = 'right' | 'left' | 'below';

/** Minimum gap (px) kept between the tooltip and the viewport edge. */
const VIEWPORT_MARGIN = 8;

const SIDE_CLASSES: Record<Side, string> = {
  right: 'left-full top-1/2 ml-2 -translate-y-1/2',
  left: 'right-full top-1/2 mr-2 -translate-y-1/2',
  below: 'top-full mt-2',
};

/**
 * Info icon with a tooltip shown on hover or keyboard focus. The tooltip is `display: none`
 * while closed so it never adds scrollable overflow, and on open it measures itself and flips to
 * the left (or below) when the default right-hand placement would run off the viewport.
 */
export function InfoTooltip({ text }: { text: string }) {
  const tooltipId = useId();
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [side, setSide] = useState<Side>('right');
  // Horizontal offset (px, relative to the icon) used only for the `below` fallback.
  const [belowLeft, setBelowLeft] = useState(0);
  const open = (hovered || focused) && !dismissed;

  // Runs before paint, so an overflowing first placement is never visible.
  useLayoutEffect(() => {
    if (!open) {
      setSide('right');
      return;
    }
    const el = tooltipRef.current;
    const anchor = el?.parentElement;
    if (!el || !anchor) return;
    // Measure in getBoundingClientRect's (visual) space so the lg+ <html> zoom cancels out.
    const viewportWidth = document.documentElement.getBoundingClientRect().width;
    const zoom = parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
    const rect = el.getBoundingClientRect();
    if (side === 'right' && rect.right > viewportWidth - VIEWPORT_MARGIN) {
      setSide('left');
    } else if (side === 'left' && rect.left < VIEWPORT_MARGIN) {
      // Neither side fits (narrow viewport): drop below, centered on the icon but clamped
      // inside the viewport.
      const a = anchor.getBoundingClientRect();
      const centered = a.left + a.width / 2 - rect.width / 2;
      const clamped = Math.min(
        Math.max(centered, VIEWPORT_MARGIN),
        viewportWidth - VIEWPORT_MARGIN - rect.width,
      );
      setBelowLeft((clamped - a.left) / zoom);
      setSide('below');
    }
  }, [open, side]);

  return (
    <span
      className="relative inline-flex align-middle"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setDismissed(false);
      }}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
          setFocused(false);
          setDismissed(false);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && open) {
          e.stopPropagation();
          setDismissed(true);
        }
      }}
    >
      <button
        type="button"
        aria-label="More info"
        aria-describedby={tooltipId}
        className="flex h-4 w-4 items-center justify-center rounded-full text-text-faint hover:text-text-default focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-blue"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2a10 10 0 100 20 10 10 0 000-20zm1 15h-2v-6h2zm0-8h-2V7h2z" />
        </svg>
      </button>
      <span
        ref={tooltipRef}
        id={tooltipId}
        role="tooltip"
        style={side === 'below' ? { left: belowLeft } : undefined}
        className={`pointer-events-none absolute z-30 w-56 max-w-[calc(100vw/var(--app-zoom)-1rem)] rounded-input border border-border bg-surface p-2.5 text-xs font-normal normal-case leading-snug tracking-normal text-text-default shadow-lg ${SIDE_CLASSES[side]} ${open ? 'block' : 'hidden'}`}
      >
        {text}
      </span>
    </span>
  );
}

/** Wraps a short in-text term (e.g. "AIU") with a native title tooltip. */
export function Term({ children, title }: { children: string; title: string }) {
  return (
    <abbr title={title} className="cursor-help decoration-dotted underline-offset-2">
      {children}
    </abbr>
  );
}
